import dayjs from "dayjs";
import { Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { AppFormField, AppFormGrid, PageSectionCard } from "../layout";
import { AppDateTimePicker } from "../form";
import { GRID_HALF } from "../../lib/ui/formLayout";
import type { SourceDateRange } from "../../utils/widgetDataSources";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

function toIso(value: dayjs.Dayjs | null): string {
  return value?.isValid() ? value.toDate().toISOString() : new Date().toISOString();
}

type Props = {
  ranges: SourceDateRange[];
  onChange: (ranges: SourceDateRange[]) => void;
  disabled?: boolean;
};

export default function WidgetSourceDateRanges({ ranges, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;

  if (ranges.length <= 1) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={1.5} sx={styles.sourceDateRangesStack}>
        <Typography variant="subtitle2">{t("widget.sourceDateRangesTitle")}</Typography>
        {ranges.map((range, index) => (
          <PageSectionCard key={range.index} title={range.index} noPadding>
            <AppFormGrid spacing="compact" sx={styles.sourceDateRangesGrid}>
              <AppFormField size={GRID_HALF}>
                <AppDateTimePicker
                  label={t("dateRange.from")}
                  value={dayjs(range.from)}
                  onChange={(value) => {
                    const next = ranges.map((item, i) =>
                      i === index ? { ...item, from: toIso(value) } : item
                    );
                    onChange(next);
                  }}
                  disabled={disabled}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppDateTimePicker
                  label={t("dateRange.to")}
                  value={dayjs(range.to)}
                  onChange={(value) => {
                    const next = ranges.map((item, i) =>
                      i === index ? { ...item, to: toIso(value) } : item
                    );
                    onChange(next);
                  }}
                  disabled={disabled}
                />
              </AppFormField>
            </AppFormGrid>
          </PageSectionCard>
        ))}
      </Stack>
    </LocalizationProvider>
  );
}
